import type { Locale, MediaKind, SupportStatus } from "./types.js";

export const copy = {
  "zh-CN": {
    languageName: "中文",
    nav: { themes: "主题目录", install: "导入指南", submit: "投稿", report: "举报", github: "GitHub" },
    menu: "菜单",
    skip: "跳到主要内容",
    heroTitle: "发现由社区认真制作的主题",
    heroBody: "查看真实效果、兼容状态和文件校验信息，再从官方 GitHub Release 下载 .ocskin。本站无账号、无广告、无追踪。",
    searchLabel: "搜索主题、作者或标签",
    searchPlaceholder: "例如：山岚、深色、OpenChatGPTSkin…",
    catalogTitle: "全部社区主题",
    resultCount: (count: number) => `${count} 个结果`,
    filters: "筛选与排序",
    allTags: "全部标签",
    allStatuses: "全部支持状态",
    allThemes: "全部主题",
    featuredOnly: "仅看精选",
    sortLabel: "排序",
    sortFeatured: "精选优先",
    sortNewest: "最新发布",
    sortUpdated: "最近更新",
    clearFilters: "清除筛选",
    noResultsTitle: "没有符合条件的主题",
    noResultsBody: "换一个关键词或清除筛选条件。目录不会用不可解释的热度排序隐藏结果。",
    emptyTitle: "首批社区主题正在公开审核",
    emptyBody: "目录基础设施已经就绪。主题只有在源目录、截图、权利声明和不可变 Release 全部通过审核后才会出现在这里。",
    viewDetails: "查看详情",
    version: "版本",
    author: "作者",
    compatibility: "兼容性",
    lastVerified: "最后验证",
    schema: "Theme Schema",
    released: "发布日期",
    fileSize: "文件大小",
    sha256: "SHA-256",
    copyHash: "复制哈希",
    copied: "已复制",
    download: "下载 .ocskin",
    downloadUnavailable: "当前版本不可下载",
    downloadHint: "下载将直接前往本项目官方 GitHub Release。Theme Studio 导入时仍会重新校验归档与 Manifest。",
    screenshots: "真实效果截图",
    versions: "版本历史",
    changelog: "更新说明",
    latest: "最新版",
    previous: "历史版本",
    rightsTitle: "权利与社区审核",
    rightsBody: "该主题的素材权利由投稿者声明。项目会检查明显风险，但收录不代表完成了独立法律核验。",
    reportTheme: "举报此主题",
    backToThemes: "返回主题目录",
    installTitle: "在 Theme Studio 中导入",
    installLead: "社区网站只负责发现与下载。安装继续使用 OpenChatGPTSkin 现有的本地安全导入链路。",
    submitTitle: "通过 Pull Request 投稿主题",
    submitLead: "提交可审查的 Theme Schema v4 源目录；不要提交预先打包的 .ocskin。可信 CI 会重新验证、打包并回读校验。",
    reportTitle: "报告权利、隐私、安全或兼容问题",
    reportLead: "一般问题使用公开 GitHub Issue；涉及未公开漏洞、个人信息或敏感材料时使用私密 Security Advisory。",
    notFoundTitle: "没有找到这个页面",
    notFoundBody: "链接可能已失效，或主题因治理原因从默认入口隐藏。已发布版本不会被同版本替换。",
    chooseLanguage: "选择浏览语言",
    chooseLanguageBody: "语言只影响界面和说明，不改变主题 ID、版本、下载地址或校验哈希。",
    footerStatement: "静态、开源、无追踪的社区主题目录。",
    footerPrivacy: "搜索和筛选只在当前浏览器中执行。",
    status: {
      verified: "已验证",
      "needs-reverification": "待复验",
      incompatible: "不兼容",
      yanked: "已下架",
    } satisfies Record<SupportStatus, string>,
    statusHelp: {
      verified: "自动校验通过，且维护者完成过真实应用验收。",
      "needs-reverification": "曾验证，但应用兼容基线变化后尚未重新验收。仍可下载。",
      incompatible: "已有证据表明当前声明版本无法正常使用，默认禁止下载。",
      yanked: "因安全、权利、隐私或严重质量问题停止分发。",
    } satisfies Record<SupportStatus, string>,
    media: {
      cover: "列表封面",
      home: "首页",
      task: "任务界面",
      conversation: "对话界面",
      light: "浅色外观",
      dark: "深色外观",
    } satisfies Record<MediaKind, string>,
  },
  en: {
    languageName: "English",
    nav: { themes: "Themes", install: "Install", submit: "Submit", report: "Report", github: "GitHub" },
    menu: "Menu",
    skip: "Skip to main content",
    heroTitle: "Discover themes made carefully by the community",
    heroBody: "Review real screenshots, compatibility and file integrity before downloading .ocskin files from the official GitHub Release. No accounts, ads or tracking.",
    searchLabel: "Search themes, authors or tags",
    searchPlaceholder: "Try mountain, dark, OpenChatGPTSkin…",
    catalogTitle: "All community themes",
    resultCount: (count: number) => `${count} result${count === 1 ? "" : "s"}`,
    filters: "Filters and sorting",
    allTags: "All tags",
    allStatuses: "All support statuses",
    allThemes: "All themes",
    featuredOnly: "Featured only",
    sortLabel: "Sort",
    sortFeatured: "Featured first",
    sortNewest: "Newest release",
    sortUpdated: "Recently updated",
    clearFilters: "Clear filters",
    noResultsTitle: "No themes match these filters",
    noResultsBody: "Try another search or clear the filters. The directory never hides results behind an unexplained popularity score.",
    emptyTitle: "The first community themes are under public review",
    emptyBody: "The directory infrastructure is ready. A theme appears only after its source, screenshots, rights declaration and immutable Release all pass review.",
    viewDetails: "View details",
    version: "Version",
    author: "Author",
    compatibility: "Compatibility",
    lastVerified: "Last verified",
    schema: "Theme Schema",
    released: "Released",
    fileSize: "File size",
    sha256: "SHA-256",
    copyHash: "Copy hash",
    copied: "Copied",
    download: "Download .ocskin",
    downloadUnavailable: "This version cannot be downloaded",
    downloadHint: "The download goes directly to this project's official GitHub Release. Theme Studio validates the archive and Manifest again during import.",
    screenshots: "Real screenshots",
    versions: "Version history",
    changelog: "Changelog",
    latest: "Latest",
    previous: "Previous release",
    rightsTitle: "Rights and community review",
    rightsBody: "Asset rights are declared by the submitter. The project checks obvious risks, but listing is not independent legal verification.",
    reportTheme: "Report this theme",
    backToThemes: "Back to themes",
    installTitle: "Import with Theme Studio",
    installLead: "The community website handles discovery and download only. Installation continues through OpenChatGPTSkin's existing local, secure import path.",
    submitTitle: "Submit a theme with a Pull Request",
    submitLead: "Submit a reviewable Theme Schema v4 source directory, not a prebuilt .ocskin. Trusted CI validates, packages and reads the archive back.",
    reportTitle: "Report rights, privacy, security or compatibility issues",
    reportLead: "Use a public GitHub Issue for ordinary reports. Use a private Security Advisory for undisclosed vulnerabilities, personal data or sensitive material.",
    notFoundTitle: "This page could not be found",
    notFoundBody: "The link may be stale, or governance may have hidden the theme from normal discovery. A published version is never replaced under the same number.",
    chooseLanguage: "Choose a language",
    chooseLanguageBody: "Language changes interface copy only. Theme IDs, versions, download URLs and integrity hashes stay the same.",
    footerStatement: "A static, open-source, tracking-free community theme directory.",
    footerPrivacy: "Search and filtering happen only in this browser.",
    status: {
      verified: "Verified",
      "needs-reverification": "Needs re-verification",
      incompatible: "Incompatible",
      yanked: "Yanked",
    } satisfies Record<SupportStatus, string>,
    statusHelp: {
      verified: "Automated checks passed and a maintainer completed a real-app acceptance check.",
      "needs-reverification": "Previously verified, but not yet rechecked after the compatibility baseline changed. Still downloadable.",
      incompatible: "Evidence shows it does not work on the declared version. Download is disabled by default.",
      yanked: "Distribution stopped for a security, rights, privacy or severe quality issue.",
    } satisfies Record<SupportStatus, string>,
    media: {
      cover: "Catalog cover",
      home: "Home",
      task: "Task view",
      conversation: "Conversation",
      light: "Light appearance",
      dark: "Dark appearance",
    } satisfies Record<MediaKind, string>,
  },
} as const;

export function text(locale: Locale) {
  return copy[locale];
}

export function tagLabel(locale: Locale, tag: string): string {
  const tags: Record<string, Record<Locale, string>> = {
    dark: { "zh-CN": "深色", en: "Dark" },
    light: { "zh-CN": "浅色", en: "Light" },
    landscape: { "zh-CN": "风景", en: "Landscape" },
    minimal: { "zh-CN": "简约", en: "Minimal" },
    colorful: { "zh-CN": "多彩", en: "Colorful" },
    anime: { "zh-CN": "动漫", en: "Anime" },
    nature: { "zh-CN": "自然", en: "Nature" },
    portrait: { "zh-CN": "人像", en: "Portrait" },
  };
  return tags[tag]?.[locale] ?? tag;
}
